import { BaseSlotGame } from '@1x2network/duck_slot_game'
import { Pot } from '@1x2network/false_persistence'
import { IdleHandler } from '@1x2network/idle_detection'
import { SymbolWin } from '@1x2network/is_engine_communication'
import { createContainer, createSprite, Specification, SpriteSpecification } from '@1x2network/pixi_assets'
import { MaxWinPopup, MediumWinPopup, TieredPopup } from '@1x2network/pixi_dialogue'
import { WinLines } from '@1x2network/pixi_win_lines'
import { GraphicMask, HoldAndWinReelSet, PositionTuple, ReelSetOrientor, TradReelSet } from '@1x2network/reel_set'
import { slotStore } from '@1x2network/slot-ui'
import { State, StateController } from '@1x2network/state_controller'
import { EffectManager, PositionEffect, Shake2D } from '@1x2network/tweens'
import { valueIds } from './constants/valueIds'
import { TrainStrikeController } from './controller/TrainStrikeController'
import { CustomSymbol, CustomSymbolSpecification } from './CustomSymbol'
import { SetupModel } from './model/SetupModel'
import { SpinModel } from './model/SpinModel'
import { SymbolModel } from './model/SymbolModel'
import { LayoutKeys } from './types/LayoutKeys'
import { filterCollectorSymbols } from './utils/filterCollectorSymbols'
import { formatCurrency, formatPixiText } from './utils/formatCurrency'
import { Anticipation } from './views/Anticipation'
import { AnticipationManager } from './views/AnticipationManager'
import { Background } from './views/Background'
import { ClickHandler } from './views/ClickHandler'
import { CoinShower } from './views/CoinShower'
import { Collection } from './views/Collection'
import { CustomSymbolPool } from './views/CustomSymbolPool'
import { EventManager } from './views/EventManager'
import { Excitement } from './views/Excitement'
import { FalsePersistence } from './views/FalsePersistence'
import { GameViews } from './views/GameViews'
import { HawCounter } from './views/HawCounter'
import { HoldAndWinCountUp } from './views/HoldAndWinCountUp'
import { IdleSymbolAnimation } from './views/IdleSymbolAnimation'
import { JackpotWidgetGroup } from './views/jackpotwidgets/JackpotWidgetGroup'
import { MiddleReel, MiddleReelSpecification } from './views/MiddleReel'
import { ReelFrame } from './views/ReelFrame'
import { SoundPlayer } from './views/SoundPlayer'
import { TrainStrikeHAWSplash } from './views/splash/TrainStrikeHAWSplash'
import { Transition } from './views/transition/Transition'

/**
 * Main game class for Train Strike Hold And Win
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class TrainStrikeHAWGame extends BaseSlotGame<GameViews, SpinModel, SetupModel, SoundPlayer, LayoutKeys> {
    protected constructSoundPlayer(): SoundPlayer {
        return new SoundPlayer()
    }

    /** construct game splash to show whilst main load taking place */
    protected constructSplash() {
        const splash = new TrainStrikeHAWSplash({ layout: this.layout, parent: this.application.stage })
        this.layout.updatePadding('splash')
        return splash
    }

    protected constructViews(): GameViews {
        const baseBackground = new Background('baseBackground', this.application.stage, this.layout)
        const hwBackground = new Background('hwBackground', this.application.stage, this.layout)

        const contents = createContainer({ parent: this.application.stage, spec: { sortableChildren: true } })
        const reelSetOrientor = new ReelSetOrientor({ spec: 'reelSetOrientor', reelSet: contents, layout: this.layout })

        const pot = new Pot({ spec: 'pot', parent: contents, layout: this.layout })
        const jackpotWidgets = new JackpotWidgetGroup(contents, this.layout)

        const pool = new CustomSymbolPool(Specification.get('symbols') as CustomSymbolSpecification[], CustomSymbol)
        const reelSet = new TradReelSet<CustomSymbol, SymbolModel>({ spec: 'reelSet', parent: contents, pool })
        const hawReelSet = new HoldAndWinReelSet<CustomSymbol, SymbolModel>({
            spec: 'hawReelSet',
            parent: contents,
            pool,
        })
        const reelSetMask = new GraphicMask({ spec: 'reelSetMask', reelSet: reelSet.spinContainer })
        const reelBackground = createSprite({
            spec: Specification.get('reelBackground') as SpriteSpecification,
            parent: reelSet,
        })
        const hawReelBackground = createSprite({
            spec: Specification.get('hawReelBackground') as SpriteSpecification,
            parent: hawReelSet,
        })
        const hawReelFrame = new ReelFrame('hawReelFrame', hawReelSet)
        const reelFrame = new ReelFrame('reelFrame', reelSet)
        const winLines = new WinLines<SymbolWin>({ spec: 'winLines', parent: reelSet })
        const falsePersistence = new FalsePersistence(pot, contents)
        const hawCounter = new HawCounter(hawReelSet)

        winLines.onLinesHidden.connect(() => {
            // Sort the symbols by row and column to prevent unexpected overlapping behaviour
            for (let column = 0; column < 5; column++) {
                const positions: PositionTuple[] = []
                for (let row = 0; row < 3; row++) {
                    positions.push([column, row])
                }
                reelSet.promoteSymbolsByPosition(positions)
            }
        })

        slotStore.subscribe(
            (state) => {
                return state.stake.current.currency
            },
            (state) => {
                jackpotWidgets.updateStake(state)
            },
            { fireImmediately: true },
        )

        const clickHandler = new ClickHandler(this.application.stage, this.layout)
        const mediumWinPopup = new MediumWinPopup({
            spec: 'mediumWinPopup',
            parent: this.application.stage,
            layout: this.layout,
            format: formatPixiText,
        })
        const tieredPopup = new TieredPopup({
            spec: 'tieredPopup',
            parent: this.application.stage,
            layout: this.layout,
            clickHandler,
            format: formatPixiText,
        })

        tieredPopup.onTierAchieved.connect((tier) => {
            if (tier === 3) {
                if (baseBackground.visible) {
                    baseBackground.playWin()
                }
                if (hwBackground.visible) {
                    hwBackground.playWin()
                }
            }
        })

        const holdAndWinCountUp = new HoldAndWinCountUp({
            fetcher: hawReelSet.fetcher,
            layout: this.layout,
            parent: contents,
            clickHandler,
        })

        const effectManager = new EffectManager()
        const reelSetEffect = new PositionEffect()
        const reelSetShake = new Shake2D(reelSetEffect)
        effectManager.addEffect(reelSet, reelSetEffect)
        effectManager.addEffect(pot, reelSetEffect)
        effectManager.addEffect(reelSet.topContainer, reelSetEffect)

        const middleReel = new MiddleReel({
            spec: Specification.get('middleReel') as MiddleReelSpecification,
            backgroundParent: reelSet,
            foregroundParent: reelSet,
        })

        const coinShower = new CoinShower({
            parent: contents,
            layout: this.layout,
            shake: reelSetShake,
            reelSet,
            effectManager,
            renderer: this.application.renderer,
            middleReel,
        })

        const eventManager = new EventManager()
        const collection = new Collection({
            spec: 'collection',
            parent: contents,
            clickHandler,
        })
        const maxWinPopup = new MaxWinPopup({
            clickHandler,
            parent: this.application.stage,
            layout: this.layout,
            currencyFormatter: formatCurrency,
            spec: 'maxWinPopup',
        })
        const transition = new Transition(this.application.stage, this.layout)
        const excitement = new Excitement(this.soundPlayer)

        effectManager.updateOrigin(hawReelSet, hawReelSet.position)
        effectManager.updateOrigin(reelSet, reelSet.position)
        this.layout.updatePadding('default')

        const anticipation = new Anticipation({ reelSet, parent: contents })
        const anticipationManager = new AnticipationManager(reelSet, anticipation, this.soundPlayer, effectManager)

        contents.addChild(
            reelSet.topContainer,
            hawReelSet.getTopContainer(0),
            hawReelSet.getTopContainer(1),
            hawReelSet.getTopContainer(2),
        )

        // Set up spin symbol middleware
        reelSet.setCreateSymbolMiddleware((symbol) => {
            const isCashSymbol = valueIds.includes(symbol.id)
            // Handle unblurring the cash symbols
            if (isCashSymbol) {
                symbol.blurred = false
                return
            }

            // No anticipation
            if (!anticipation.isActive) {
                return
            }

            // Fade non-cash symbols during anticipation
            symbol.tint = 0x7f7f7f
        })

        const idleHandler = new IdleHandler()
        const idleSymbolAnimation = new IdleSymbolAnimation(idleHandler, reelSet)

        slotStore.subscribe(
            (state) => state.settings['quick-spin'].value,
            (state) => {
                if (state) {
                    reelSet.spinAnimator.setStagger('start', 0)
                } else {
                    reelSet.spinAnimator.resetStagger()
                }
            },
            { fireImmediately: true },
        )

        const views = {
            pool,
            coinShower,
            soundPlayer: this.soundPlayer,
            reelFrame,
            reelSet,
            hawReelSet,
            reelSetMask,
            reelBackground,
            baseBackground,
            hwBackground,
            pot,
            winLines,
            falsePersistence,
            transition,
            holdAndWinCountUp,
            clickHandler,
            mediumWinPopup,
            tieredPopup,
            jackpotWidgets,
            shake: reelSetShake,
            eventManager,
            collection,
            maxWinPopup,
            hawCounter,
            excitement,
            effectManager,
            hawReelFrame,
            hawReelBackground,
            anticipation,
            anticipationManager,
            idleHandler,
            idleSymbolAnimation,
            middleReel,
        }

        this.initSoundListeners(views)

        return views
    }

    private initSoundListeners(views: GameViews): void {
        const { soundPlayer, reelSet, hawReelSet, tieredPopup, pot, falsePersistence } = views

        views.middleReel.onAnimate.connect(() => {
            soundPlayer.playTrainTrackBuilding()
        })

        views.coinShower.onTrainVisible.connect(() => {
            soundPlayer.playTrainPassing()
        })

        views.coinShower.onTrainHidden.connect(() => {
            soundPlayer.stopTrainPassing()
        })

        falsePersistence.tierChange.connect((tier) => {
            soundPlayer.playFalsePersistenceLoop(tier)
        })
        soundPlayer.playFalsePersistenceLoop(falsePersistence.tier)

        reelSet.spinAnimator.signals.onStart.connect((index) => {
            if (index === 0) {
                soundPlayer.playSpinStart()
            }
        })

        let reelsWithTriggerSymbols = 0
        let hasCollectorLanded = false
        let totalScattersLanded = 0
        let hasAnimatedPotIncrease = false
        reelSet.spinAnimator.signals.onLand.connect((index) => {
            soundPlayer.playReelLand()
            const symbols = reelSet.fetcher.getAllSymbolsByReel(index)
            const triggerSymbols = symbols.filter((s) => valueIds.includes(s.id))
            const collectorSymbols = filterCollectorSymbols(triggerSymbols)
            const amountOfScatters = triggerSymbols.length - collectorSymbols.length
            const hasCollector = Boolean(collectorSymbols.length)

            if (hasCollector) {
                hasCollectorLanded = true
            }

            if (amountOfScatters > 0) {
                totalScattersLanded += amountOfScatters
            }

            if (hasCollectorLanded && totalScattersLanded >= 4 && !hasAnimatedPotIncrease) {
                hasAnimatedPotIncrease = true
                falsePersistence.countUpTo(falsePersistence.maxTier - 1)
            }

            if (triggerSymbols.length) {
                if (hasCollector) {
                    soundPlayer.playCollectorLand(reelsWithTriggerSymbols)
                } else {
                    this.soundPlayer.playScatterLand(reelsWithTriggerSymbols)
                }
                reelsWithTriggerSymbols++
            }
        })

        reelSet.spinAnimator.signals.onAllComplete.connect(() => {
            reelsWithTriggerSymbols = 0
            hasCollectorLanded = false
            totalScattersLanded = 0
            hasAnimatedPotIncrease = false
        })

        let newHawSpin = true
        let scattersLandedThisSpin = 0
        hawReelSet.spinAnimator.signals.onStart.connect(() => {
            if (newHawSpin) {
                soundPlayer.playSpinStart()
                newHawSpin = false
            }
        })

        hawReelSet.spinAnimator.signals.onAllComplete.connect(() => {
            newHawSpin = true
            scattersLandedThisSpin = 0
            collectorsThisSpin = 0
        })

        hawReelSet.spinAnimator.signals.onLand.connect((index) => {
            const reel = index % 5
            const row = Math.floor(index / 5)
            const id = hawReelSet.fetcher.getSymbolByPosition([reel, row]).id

            if (valueIds.includes(id)) {
                soundPlayer.playScatterLand(scattersLandedThisSpin++)
            } else {
                soundPlayer.playReelLand()
            }
        })

        tieredPopup.onStart.connect(() => {
            soundPlayer.fadeBackground(0.5)
        })

        tieredPopup.onTierAchieved.connect((tier) => {
            soundPlayer.stopWinTier(tier - 1)
            soundPlayer.playWinTier(tier)
        })

        tieredPopup.onExit.connect((tier) => {
            soundPlayer.fadeBackground(1)
            soundPlayer.stopWinTier(tier)
        })

        pot.onWin.connect(() => {
            soundPlayer.playPotAnimate(reelsWithTriggerSymbols)
        })

        pot.onUpgrade.connect(() => {
            soundPlayer.playPotUpgrade()
        })

        let collectorsThisSpin = 0
        let collectCount = 0
        views.collection.onActivate.connect(() => {
            collectorsThisSpin++
            collectCount = 0
            soundPlayer.playCollectorTrigger()
        })

        views.collection.onCollect.connect(() => {
            soundPlayer.playCollectorIncrease(collectorsThisSpin * 2 + collectCount++)
        })

        views.coinShower.onEmit.connect(() => {
            soundPlayer.playCoinShowerEmit()
        })

        views.coinShower.onLand.connect(() => {
            soundPlayer.playCoinShowerLand()
        })

        views.holdAndWinCountUp.onCollect.connect(() => {
            soundPlayer.playHawSummaryIncrease()
        })

        views.tieredPopup.onCountupStart.connect(() => {
            soundPlayer.playCountUpLoop()
        })

        views.tieredPopup.onCountupEnd.connect(() => {
            soundPlayer.stopCountUpLoop()
            soundPlayer.playCountUpEnd()
        })
    }

    protected constructController(): StateController<GameViews, State<GameViews, SpinModel, SetupModel>[]> {
        return new TrainStrikeController(this.views)
    }
}
